import React, { useState } from 'react';
import { Modal, Steps, Form, Input, Select, Switch, Button, Divider, Typography, message, Card, Alert, Tabs, Collapse, InputNumber, Tag } from 'antd';
import { ApiOutlined, LinkOutlined, SettingOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

const ProviderWizard = ({ visible, onCancel, onSuccess, editingProvider = null }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [apiType, setApiType] = useState('openai'); // 'openai', 'claude', 'gemini', 'custom'

  const isEdit = !!editingProvider;

  // 预设供应商地址配置（可扩展）
  const presetEndpoints = {
    openai: [
      // 官方平台
      { name: 'OpenAI', url: 'https://api.openai.com/v1' },
      { name: 'DeepSeek', url: 'https://api.deepseek.com' },
      { name: '通义千问', url: 'https://dashscope.aliyuncs.com/compatible-mode/v1' },
      { name: '智谱AI', url: 'https://open.bigmodel.cn/api/paas/v4' },
      { name: 'Moonshot (Kimi)', url: 'https://api.moonshot.cn/v1' },
      { name: 'MiniMax', url: 'https://api.minimax.chat/v1' },
      { name: '字节豆包', url: 'https://ark.cn-beijing.volces.com/api/v3' },
      // 代理/聚合服务
      { name: 'Groq', url: 'https://api.groq.com/openai/v1' },
      { name: 'Together AI', url: 'https://api.together.xyz/v1' },
      { name: 'SiliconFlow (硅基流动)', url: 'https://api.siliconflow.cn/v1' },
      { name: 'Cohere', url: 'https://api.cohere.ai/v1' },
      { name: 'Mistral', url: 'https://api.mistral.ai/v1' },
      { name: 'Novita AI', url: 'https://api.novita.ai/v3' },
      { name: 'Hyperbolic', url: 'https://api.hyperbolic.ai/v1' },
    ],
    claude: [
      { name: 'Anthropic (官方)', url: 'https://api.anthropic.com' },
      { name: 'Claude via OpenRouter', url: 'https://openrouter.ai/api/v1' },
    ],
    gemini: [
      { name: 'Google AI (官方)', url: 'https://generativelanguage.googleapis.com/v1beta' },
      { name: 'Gemini via OpenRouter', url: 'https://openrouter.ai/api/v1' },
    ],
    openrouter: [
      { name: 'OpenRouter (官方)', url: 'https://openrouter.ai/api/v1' },
    ],
    custom: [], // 自定义模板不使用预设
  };

  // 适配器类型定义
  const adapterTypes = [
    {
      value: 'openai',
      label: 'OpenAI 兼容',
      icon: '🤖',
      desc: '适用于 DeepSeek、智谱AI、Moonshot、MiniMax、通义千问等标准 OpenAI 格式',
      color: '#10a37f',
    },
    {
      value: 'claude',
      label: 'Anthropic (Claude)',
      icon: '🧠',
      desc: '适用于 Claude 系列模型，使用 Claude 专用 API 格式',
      color: '#cc785c',
    },
    {
      value: 'gemini',
      label: 'Google (Gemini)',
      icon: '✨',
      desc: '适用于 Gemini 系列模型，使用 Google AI API 格式',
      color: '#5db87f',
    },
    {
      value: 'openrouter',
      label: 'OpenRouter',
      icon: '🌐',
      desc: '适用于 OpenRouter 代理，支持全球多种模型，带自动加价',
      color: '#7c3aed',
    },
    {
      value: 'custom',
      label: '自定义模板（高级）',
      icon: '⚙️',
      desc: '适用于非标准 API，需要配置请求/响应模板',
      color: '#666',
    },
  ];

  // 初始化表单
  React.useEffect(() => {
    if (visible) {
      setCurrentStep(0);
      setTestResult(null);
      if (editingProvider) {
        form.setFieldsValue(editingProvider);
        // 根据 adapter_type 和 is_custom 判断类型
        if (editingProvider.adapter_type === 'claude') {
          setApiType('claude');
        } else if (editingProvider.adapter_type === 'gemini') {
          setApiType('gemini');
        } else if (editingProvider.adapter_type === 'openrouter') {
          setApiType('openrouter');
        } else if (editingProvider.is_custom || editingProvider.adapter_type === 'custom') {
          setApiType('custom');
        } else {
          setApiType('openai');
        }
      } else {
        form.resetFields();
        setApiType('openai');
      }
    }
  }, [visible, editingProvider]);

  // 步骤定义
  const steps = [
    {
      title: '基础信息',
      icon: <ApiOutlined />,
      description: '配置供应商基本信息',
    },
    {
      title: 'API配置',
      icon: <SettingOutlined />,
      description: '选择API类型并配置',
    },
    {
      title: '测试连接',
      icon: <LinkOutlined />,
      description: '验证配置是否正确',
    },
    {
      title: '确认保存',
      icon: <CheckCircleOutlined />,
      description: '确认信息并保存',
    },
  ];

  // 步骤内容
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Alert
              message="基本信息配置"
              description="这些信息用于标识和连接AI供应商。大部分AI服务商的API都是OpenAI兼容格式，只需填写基础信息即可。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              name="name"
              label="供应商名称"
              rules={[{ required: true, message: '请输入供应商名称' }]}
              tooltip="用于系统内部标识，建议使用英文，如：deepseek, zhipu, openai"
            >
              <Input 
                placeholder="如: deepseek" 
                disabled={isEdit}
                suffix={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    内部标识
                  </Text>
                }
              />
            </Form.Item>

            <Form.Item
              name="display_name"
              label="显示名称"
              rules={[{ required: true, message: '请输入显示名称' }]}
              tooltip="展示给用户看的名称，可以使用中文，如：DeepSeek, 智谱AI"
            >
              <Input 
                placeholder="如: DeepSeek, 智谱AI"
                suffix={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    用户可见
                  </Text>
                }
              />
            </Form.Item>

            <Form.Item
              name="api_base"
              label="API地址 (Base URL)"
              rules={[{ required: true, message: '请输入API地址' }]}
              tooltip="AI服务商的API基础地址，不包含具体的接口路径"
            >
              <Input 
                placeholder="如: https://api.deepseek.com"
                prefix={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    URL
                  </Text>
                }
              />
            </Form.Item>

            {/* 预设供应商快速选择 */}
            <Form.Item 
              name="preset_endpoint" 
              label="快速选择"
              extra="选择预设供应商自动填充地址，也可手动修改"
            >
              <Select
                placeholder="点击选择预设供应商..."
                allowClear
                showSearch
                options={presetEndpoints[apiType]?.map(ep => ({
                  value: ep.url,
                  label: ep.name,
                })) || []}
                onChange={(url) => {
                  if (url) {
                    form.setFieldsValue({ api_base: url });
                  }
                }}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
              <strong>如何获取API地址？</strong><br />
              1. 访问AI服务商官网（如 platform.deepseek.com）<br />
              2. 查看API文档，找到 Base URL<br />
              3. 常见地址：<br />
              &nbsp;&nbsp;- DeepSeek: https://api.deepseek.com<br />
              &nbsp;&nbsp;- 通义千问: https://dashscope.aliyuncs.com/compatible-mode/v1<br />
              &nbsp;&nbsp;- 智谱AI: https://open.bigmodel.cn/api/paas/v4<br />
              &nbsp;&nbsp;- OpenAI: https://api.openai.com/v1
            </Paragraph>

            <Form.Item
              name="api_key"
              label="API密钥 (API Key)"
              rules={[{ required: !isEdit, message: '请输入API密钥' }]}
              tooltip="从AI服务商获取的API密钥"
            >
              <Input.Password 
                placeholder="如: sk-xxxxxxxxxxxxxxxx"
                prefix={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Key
                  </Text>
                }
              />
            </Form.Item>

            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
              <strong>如何获取API Key？</strong><br />
              1. 登录AI服务商控制台<br />
              2. 找到"API Keys"或"密钥管理"<br />
              3. 创建新的API Key并复制<br />
              <Text type="warning">注意：API Key是敏感信息，请妥善保管！</Text>
            </Paragraph>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item
                name="priority"
                label="优先级"
                initialValue={0}
                tooltip="数字越大优先级越高，当多个供应商都有同一个模型时，优先使用高优先级的供应商"
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  max={100}
                  placeholder="0-100，默认0"
                />
              </Form.Item>

              {isEdit && (
                <Form.Item
                  name="status"
                  label="状态"
                  initialValue="active"
                >
                  <Select
                    options={[
                      { value: 'active', label: '启用' },
                      { value: 'inactive', label: '禁用' },
                    ]}
                  />
                </Form.Item>
              )}
            </div>

            <Form.Item
              name="provider_type"
              label="供应商类型"
              initialValue="internal"
              tooltip="内部供应商模型名直接使用；外部供应商模型名 = 供应商名/模型名，避免同名冲突"
            >
              <Select
                options={[
                  { value: 'internal', label: '内部供应商（默认）' },
                  { value: 'external', label: '外部供应商（模型ID: 供应商名/模型名）' },
                ]}
              />
            </Form.Item>
          </div>
        );

      case 1:
        return (
          <div>
            <Alert
              message="API类型配置"
              description="选择您的AI服务商API类型，系统会根据类型自动配置相应的请求格式和适配器。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            {/* 隐藏的 adapter_type 字段 */}
            <Form.Item name="adapter_type" hidden>
              <Input />
            </Form.Item>

            <div style={{ marginBottom: 24 }}>
              {/* 适配器类型选择 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {adapterTypes.map((type) => (
                  <Card 
                    key={type.value}
                    size="small" 
                    style={{ 
                      borderColor: apiType === type.value ? type.color : '#d9d9d9',
                      backgroundColor: apiType === type.value ? `${type.color}10` : '#fff',
                      cursor: 'pointer',
                      borderWidth: apiType === type.value ? 2 : 1,
                    }}
                    onClick={() => {
                      setApiType(type.value);
                    }}
                    hoverable
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span style={{ fontSize: 24 }}>{type.icon}</span>
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ color: type.color }}>{type.label}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {type.desc}
                        </Text>
                      </div>
                      <input 
                        type="radio" 
                        checked={apiType === type.value} 
                        onChange={() => {
                          setApiType(type.value);
                        }}
                        style={{ marginTop: 2 }}
                      />
                    </div>
                  </Card>
                ))}
              </div>

              {/* 适配器类型提示 */}
              {apiType === 'claude' && (
                <Alert
                  message="已选择 Claude 适配器"
                  description={
                    <div>
                      <p style={{ marginBottom: 4 }}>
                        Claude 使用专用 API 格式，请在下方选择或输入 API 地址
                      </p>
                      <p style={{ marginBottom: 0 }}>
                        <strong>支持的模型：</strong>claude-3.5-sonnet, claude-3-opus, claude-3-haiku 等
                      </p>
                    </div>
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {apiType === 'gemini' && (
                <Alert
                  message="已选择 Gemini 适配器"
                  description={
                    <div>
                      <p style={{ marginBottom: 4 }}>
                        Gemini 使用 Google AI API 格式，请在下方选择或输入 API 地址
                      </p>
                      <p style={{ marginBottom: 0 }}>
                        <strong>支持的模型：</strong>gemini-2.0-flash, gemini-1.5-pro, gemini-1.5-flash 等
                      </p>
                    </div>
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* OpenAI 兼容的提示 */}
              {apiType === 'openai' && (
                <Alert
                  message="OpenAI 兼容格式"
                  description={
                    <div>
                      <p style={{ marginBottom: 4 }}>
                        大部分 AI 服务商都兼容 OpenAI 格式，请在上方选择一个预设供应商或手动输入 API 地址
                      </p>
                      <p style={{ marginBottom: 0 }}>
                        <strong>支持的供应商：</strong>DeepSeek、通义千问、智谱AI、Moonshot、MiniMax、字节豆包、OpenAI 等
                      </p>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
            </div>

            {apiType === 'custom' && (
              <div>
                <Divider plain style={{ fontWeight: 'bold', color: '#1890ff' }}>自定义模板配置（高级）</Divider>

                <Form.Item name="is_custom" initialValue={true} hidden>
                  <Input />
                </Form.Item>

                <Divider plain style={{ fontWeight: 'bold', color: '#1890ff' }}>HTTP 请求配置</Divider>

                <Form.Item name="request_method" label="HTTP 方法" initialValue="POST">
                  <Select options={[
                    { value: 'GET', label: 'GET' },
                    { value: 'POST', label: 'POST' },
                    { value: 'PUT', label: 'PUT' },
                    { value: 'DELETE', label: 'DELETE' },
                  ]} />
                </Form.Item>

                <Form.Item name="request_url_template" label="完整URL模板"
                  extra="支持变量: {PROVIDER_KEY}=API密钥, {MODEL}=模型名, {INPUT}=用户输入"
                >
                  <Input placeholder="https://api.example.com/v1/chat?key={PROVIDER_KEY}" />
                </Form.Item>

                <Form.Item name="request_headers" label="请求头 (JSON)">
                  <TextArea 
                    rows={3} 
                    placeholder={`{\n  "Content-Type": "application/json",\n  "Authorization": "Bearer {PROVIDER_KEY}"\n}`}
                  />
                </Form.Item>

                <Form.Item name="request_body_template" label="请求体模板 (JSON)">
                  <TextArea 
                    rows={5} 
                    placeholder={`{\n  "model": "{MODEL}",\n  "messages": [{"role": "user", "content": "{INPUT}"}]\n}`}
                  />
                </Form.Item>

                <Divider plain style={{ fontWeight: 'bold', color: '#1890ff' }}>响应配置</Divider>

                <Form.Item name="response_result_path" label="响应结果字段路径" initialValue="choices.0.message.content"
                  extra="用于提取API返回的主要结果，如: choices.0.message.content, result, data.content"
                >
                  <Input placeholder="choices.0.message.content" />
                </Form.Item>
              </div>
            )}

            {/* 默认模型能力配置 */}
            <Divider plain style={{ fontWeight: 'bold', color: '#1890ff' }}>默认模型能力配置</Divider>

            <Card size="small" style={{ backgroundColor: '#f6ffed', marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                这些是此供应商下模型的默认能力配置。添加模型时可以覆盖此设置。
              </Text>
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <Form.Item
                name="context_window"
                label="上下文窗口大小"
                tooltip="此供应商下模型的默认上下文窗口大小（tokens），如：4096, 8192, 128000"
                initialValue={4096}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1024}
                  placeholder="4096"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/,/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="tools_support"
                label="默认函数调用支持"
                valuePropName="checked"
                initialValue={true}
                tooltip="此供应商下模型是否默认支持函数调用/工具使用"
              >
                <Switch checkedChildren="支持" unCheckedChildren="不支持" />
              </Form.Item>

              <Form.Item
                name="vision_support"
                label="默认视觉支持"
                valuePropName="checked"
                initialValue={false}
                tooltip="此供应商下模型是否默认支持图像理解（视觉）"
              >
                <Switch checkedChildren="支持" unCheckedChildren="不支持" />
              </Form.Item>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <Alert
              message="测试连接"
              description="点击测试按钮，系统将使用您配置的API地址和密钥发送一个测试请求，验证配置是否正确。测试仅消耗极少token（约5-10个）。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Card size="small" style={{ marginBottom: 24 }}>
              <p><strong>API地址：</strong> {form.getFieldValue('api_base')}</p>
              <p><strong>API密钥：</strong> {form.getFieldValue('api_key') ? '●●●●●●●●' + form.getFieldValue('api_key').slice(-4) : '未设置'}</p>
              
              <Divider plain>测试选项</Divider>
              
              <Input
                placeholder="输入要测试的模型名称（如：gpt-3.5-turbo、claude-3-haiku-20240307）"
                defaultValue="gpt-3.5-turbo"
                style={{ marginBottom: 12 }}
                onChange={(e) => form.setFieldsValue({ test_model: e.target.value })}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                提示：请输入您供应商支持的模型名称。OpenRouter 可输入 'google/palm-2-chat-bison' 等，OpenAI 可输入 'gpt-3.5-turbo'
              </Text>
            </Card>

            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Button 
                type="primary" 
                size="large"
                icon={testing ? <LoadingOutlined /> : <LinkOutlined />}
                onClick={handleTestConnection}
                loading={testing}
                disabled={!form.getFieldValue('api_base') || !form.getFieldValue('api_key')}
              >
                {testing ? '测试中...' : '开始测试'}
              </Button>
            </div>

            {testResult && (
              <div>
                <Alert
                  message={testResult.success ? '✅ 测试成功' : '❌ 测试失败'}
                  description={testResult.message}
                  type={testResult.success ? 'success' : 'error'}
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Divider>请求详情</Divider>
                <Card size="small" title="请求信息" style={{ marginBottom: 16 }}>
                  <p><strong>URL: </strong> {testResult.request?.url}</p>
                  <p><strong>Method: </strong> {testResult.request?.method}</p>
                </Card>
                <Card size="small" title="请求头 (Headers)" style={{ marginBottom: 16 }}>
                  <pre style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto' }}>
                    {JSON.stringify(testResult.request?.headers || {}, null, 2)}
                  </pre>
                </Card>

                <Card size="small" title="请求体 (Body)" style={{ marginBottom: 16 }}>
                  <pre style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto' }}>
                    {JSON.stringify(testResult.request?.body || {}, null, 2)}
                  </pre>
                </Card>

                <Divider>响应详情</Divider>
                <Card size="small" title="响应状态码" style={{ marginBottom: 16 }}>
                  <Tag color={testResult.response?.status < 400 ? 'green' : 'red'}>
                    {testResult.response?.status} {testResult.response?.status_text}
                  </Tag>
                  {testResult.response?.response_time_ms && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      响应时间: {testResult.response.response_time_ms}ms
                    </Text>
                  )}
                </Card>

                <Card size="small" title="响应体 (Response Body)">
                  <pre style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto', maxHeight: 300 }}>
                    {JSON.stringify(testResult.response?.data || testResult.response?.raw || testResult.response?.error || {}, null, 2)}
                  </pre>
                </Card>
              </div>
            )}

            {!testResult && (
              <Alert
                message="测试说明"
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    <li>测试将使用您的API Key发送一个简单请求</li>
                    <li>不会消耗您的额度（或只消耗极少额度）</li>
                    <li>如果测试失败，请检查：API地址、API Key、网络连通性</li>
                    <li>测试成功后，您可以继续下一步保存配置</li>
                  </ul>
                }
                type="info"
                showIcon
              />
            )}
          </div>
        );

      case 3:
        return (
          <div>
            <Alert
              message="确认配置信息"
              description="请仔细核对以下配置信息，确认无误后点击保存。"
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Card size="small" title="基础信息">
              <p><strong>供应商名称：</strong> {form.getFieldValue('name')}</p>
              <p><strong>显示名称：</strong> {form.getFieldValue('display_name')}</p>
              <p><strong>API地址：</strong> {form.getFieldValue('api_base')}</p>
              <p><strong>优先级：</strong> {form.getFieldValue('priority') || 0}</p>
              <p><strong>适配器类型：</strong> {
                apiType === 'openai' ? '🤖 OpenAI 兼容' :
                apiType === 'claude' ? '🧠 Anthropic (Claude)' :
                apiType === 'gemini' ? '✨ Google (Gemini)' :
                apiType === 'openrouter' ? '🌐 OpenRouter' :
                '⚙️ 自定义模板'
              }</p>
            </Card>

            {testResult && (
              <Card size="small" title="测试结果" style={{ marginTop: 16 }}>
                <p>
                  <strong>状态：</strong> 
                  <span style={{ color: testResult.success ? '#52c41a' : '#ff4d4f' }}>
                    {testResult.success ? '✅ 测试成功' : '❌ 测试失败'}
                  </span>
                </p>
                {testResult.success && (
                  <p style={{ color: '#52c41a' }}>
                    配置验证通过，可以安全保存。
                  </p>
                )}
              </Card>
            )}

            {!testResult && (
              <Alert
                message="未测试连接"
                description="建议您先测试连接，确保配置正确后再保存。"
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
                action={
                  <Button size="small" onClick={() => setCurrentStep(2)}>
                    去测试
                  </Button>
                }
              />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // 测试连接
  const handleTestConnection = async () => {
    try {
      // 获取所有表单值（不验证，因为字段分布在多个步骤）
      const values = form.getFieldsValue(true);
      setTesting(true);
      setTestResult(null);

      console.log('[ProviderWizard] 表单值:', values);
      
      // 验证必填字段
      if (!values.api_base || !values.api_key) {
        message.error('请先填写 API 地址和 API 密钥（第1步）');
        setTesting(false);
        return;
      }

      const testData = {
        api_base: values.api_base,
        api_key: values.api_key,
        test_model: values.test_model || (() => {
          switch (apiType) {
            case 'claude': return 'claude-3-5-sonnet-20241022';
            case 'gemini': return 'gemini-2.5-flash';
            case 'openrouter': return 'openrouter/anthropic/claude-3.5-sonnet';
            default: return 'gpt-3.5-turbo';
          }
        })(),
        adapter_type: apiType, // 适配器类型：openai/claude/gemini/openrouter/custom
        is_custom: apiType === 'custom',
        request_method: values.request_method || 'POST',
        request_url_template: values.request_url_template || '',
        request_headers: values.request_headers || '',
        request_body_template: values.request_body_template || '',
        response_result_path: values.response_result_path || '',
      };

      console.log('[ProviderWizard] 发送测试数据:', testData);

      const result = await adminAPI.testProviderConnection(testData);
      console.log('[TestProvider] API result:', result);

      // result = { code, message, data }  ← 外层包装（响应拦截器已解包一层）
      // result.data = { code, message, data }  ← 内层测试结果
      // result.data.data = { request: {...}, response: {...} }  ← 实际请求/响应详情
      const testDetail = result.data?.data || {};
      const testSuccess = testDetail?.response?.status >= 200 && testDetail?.response?.status < 300;

      setTestResult({
        success: testSuccess,
        message: result.data?.message || result.message || (testSuccess ? '连接成功' : '连接失败'),
        request: testDetail?.request || {},
        response: testDetail?.response || {},
      });
      console.log('[TestProvider] testDetail:', testDetail);

      if (testSuccess) {
        message.success('测试成功！配置正确。');
      } else {
        message.error(result.data?.message || result.message || '测试失败，请检查配置');
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error.message || '测试失败',
        request: {},
        response: { error: error.message },
      });
      message.error('测试失败：' + (error.message || error));
    } finally {
      setTesting(false);
    }
  };

  // 下一步
  const handleNext = async () => {
    if (currentStep === 0) {
      try {
        await form.validateFields(['name', 'display_name', 'api_base', 'api_key']);
        setCurrentStep(currentStep + 1);
      } catch (error) {
        message.warning('请填写完整的必填项');
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  // 上一步
  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  // 提交
  const handleSubmit = async () => {
    try {
      // 使用 getFieldsValue 而不是 validateFields，因为确认步骤没有 Form.Item 在 DOM 中
      const values = form.getFieldsValue(true);
      setLoading(true);

      const submitData = {
        ...values,
        adapter_type: apiType, // 适配器类型：openai/claude/gemini/custom
        is_custom: apiType === 'custom',
      };

      let response;
      if (isEdit) {
        response = await adminAPI.updateProvider(editingProvider.id, submitData);
      } else {
        response = await adminAPI.createProvider(submitData);
      }

      if (response.code === 0) {
        message.success(isEdit ? '更新成功' : '创建成功');
        onSuccess();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      message.error('操作失败：' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span>
          <ApiOutlined style={{ marginRight: 8 }} />
          {isEdit ? '编辑供应商' : '添加供应商向导'}
        </span>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onCancel}>取消</Button>
          <div>
            {currentStep > 0 && (
              <Button style={{ marginRight: 8 }} onClick={handlePrev}>
                上一步
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button type="primary" onClick={handleNext}>
                下一步
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                保存
              </Button>
            )}
          </div>
        </div>
      }
    >
      <Steps 
        current={currentStep} 
        items={steps}
        style={{ marginBottom: 32 }}
      />

      <Form form={form} layout="vertical">
        {renderStepContent()}
      </Form>
    </Modal>
  );
};

export default ProviderWizard;
